
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ListPlus } from "lucide-react";

interface Source {
  description: string;
  url: string;
}

interface SourceManagerProps {
  sources: Source[];
  onChange: (sources: Source[]) => void;
  isLoading?: boolean;
}

export const SourceManager: React.FC<SourceManagerProps> = ({
  sources,
  onChange,
  isLoading = false
}) => {
  const [sourceIndex, setSourceIndex] = useState(-1);
  const [sourceDescription, setSourceDescription] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");

  const handleSourceEdit = (index: number) => {
    setSourceIndex(index);
    setSourceDescription(sources[index]?.description || "");
    setSourceUrl(sources[index]?.url || "");
  };

  const handleSourceSave = () => {
    if (sourceIndex !== -1) {
      const updatedSources = [...sources];
      updatedSources[sourceIndex] = { description: sourceDescription, url: sourceUrl };
      onChange(updatedSources);
      setSourceIndex(-1);
      setSourceDescription("");
      setSourceUrl("");
    }
  };

  const handleSourceCancel = () => {
    setSourceIndex(-1);
    setSourceDescription("");
    setSourceUrl("");
  };

  // Create a new empty source and set it to edit mode
  const handleSourceAdd = () => {
    const updatedSources = [...sources, { description: "", url: "" }];
    onChange(updatedSources);
    
    // Set the index to the newly added source for immediate editing
    setSourceIndex(updatedSources.length - 1);
    setSourceDescription("");
    setSourceUrl("");
  };

  const handleSourceRemove = (index: number) => {
    const updatedSources = sources.filter((_, i) => i !== index);
    onChange(updatedSources);
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button 
          type="button" 
          onClick={handleSourceAdd}
          disabled={isLoading}
          variant="outline"
          size="sm"
          className="gap-1"
        >
          <ListPlus className="h-4 w-4" />
          Add Source
        </Button>
      </div>

      {sources.length === 0 ? (
        <div className="text-center py-6 text-slate-500">
          <p>No sources added yet. Add sources to reference external content.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sources.map((source, index) => (
            <Card key={index} className="p-4 space-y-2 border border-slate-200">
              {sourceIndex === index ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor={`source-description-${index}`}>Description</Label>
                    <Input
                      id={`source-description-${index}`}
                      placeholder="Description"
                      value={sourceDescription}
                      onChange={(e) => setSourceDescription(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`source-url-${index}`}>URL</Label>
                    <Input
                      id={`source-url-${index}`}
                      placeholder="URL"
                      value={sourceUrl}
                      onChange={(e) => setSourceUrl(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button type="button" onClick={handleSourceSave} disabled={isLoading}>
                      Save
                    </Button>
                    <Button type="button" variant="ghost" onClick={handleSourceCancel} disabled={isLoading}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium">{source.description || "No Description"}</p>
                  <p className="text-sm text-muted-foreground">{source.url || "No URL"}</p>
                  <div className="flex justify-end space-x-2">
                    <Button type="button" onClick={() => handleSourceEdit(index)} disabled={isLoading} size="sm" variant="outline">
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleSourceRemove(index)}
                      disabled={isLoading}
                      className="text-red-500 hover:text-red-600"
                    >
                      Remove
                    </Button>
                  </div>
                </>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
